// THE OPTIONS EVERY PEOPLE PICKER SHOWS, built from one roster.
import { matchOption } from "components/SearchSelect/searchSelect";
//
// Owner, 2026-10-15: "everywhere we can assign or recepient, the dropdown
// now needs to show all partners, their discipline on the partner header,
// their users, supervisor and normal users".
//
// The roster is GET /projects/:id/participants/roster (back/utils/
// pickerRoster): owner company first, then each active partner, each group
// carrying `people` tagged by kind. This file turns that into antd Select
// option groups - "BES - Electrical" as the header, the people under it -
// and it is pure, so every picker and its tests share exactly one answer.
//
// THE FIELD DECIDES THE VALUE, NOT THE PICKER. A responsible field stores
// user ids, a shop drawing recipient stores an email, a schedule row stores
// a company id. Each caller says what its field holds:
//
//   personValue(person, group)  the option value for a person, or '' when
//                               this field cannot hold them
//   companyValue(group)         the value for the WHOLE company (only for a
//                               field that can hold a company), else omit
//   noAccount                   'allow' | 'disable' - may a person with no
//                               account be picked here (a field that stores
//                               user ids cannot hold one)
//   note(person)                a few words shown on the row, for a field
//                               whose page has its own approved wording
//                               (shop drawings: "on this project")
//   executives                  'allow' | 'disable' - an executive is a
//                               READER AND RECIPIENT only (owner,
//                               2026-09-20), so a responsible field greys him
//                               out; a recipient field may send him things
//
// NOBODY DISAPPEARS. Someone a field cannot hold is listed DISABLED, with
// the reason in words, rather than left out: a dropdown that quietly shows
// fewer people reads as a project with fewer people on it.

export const KIND_LABEL = {
  supervisor: "Supervisor",
  user: "User",
  employee: "Employee",
  executive: "Executive",
};

export const NO_ACCOUNT_REASON = "No account yet - this field needs someone who can sign in";
// NAME-ONLY PEOPLE (owner, 2026-10-15: "make sure all dropdowns allow
// assigning can see them"): somebody on a partner's list with no address yet.
// Every field here stores a user id or an address, so they are shown greyed
// out with this reason (the server sends its own wording, which wins) - and
// a field that takes a free contact opts in with nameOnly: "allow" and a
// personValue that can hold a name.
export const NEEDS_EMAIL_REASON = "Needs an email - add it on the Partners page to assign or send to this person";
// A PARTNER WITH NOBODY LISTED still appears (same day: "every partner on
// the project appears"), as a header with this one inert line, unless the
// field can hold the whole company - then the company row is the option.
export const EMPTY_PARTNER_LABEL = "Nobody listed yet";
export const EMPTY_PARTNER_REASON = "No people on this partner yet - add them on the Partners page";

// The legacy roster (before `people` existed) carried only `users`. Read it
// the same way, so a picker never shows less against an older server.
export function peopleOf(group) {
  if (group && Array.isArray(group.people)) return group.people;
  return ((group && group.users) || []).map((u) => ({
    // `_id` / `username`: the shape some older callers (and fixtures) use.
    userId: u.userId || u._id ? String(u.userId || u._id) : "",
    name: u.name || u.username || u.email,
    email: String(u.email || "").toLowerCase(),
    kind: u.kind || (u.contact && /supervisor/i.test(u.role || "") ? "supervisor" : (u.contact ? "user" : "employee")),
    needsEmail: !u.email && !(u.userId || u._id),
    jobTitle: u.jobTitle || "",
    hasAccount: !!(u.userId || u._id),
    paused: !!u.paused,
    assignable: true,
  }));
}

export function groupLabel(group, { ownerLabel } = {}) {
  if (!group) return "";
  if (group.label) return group.role === "owner" && ownerLabel ? `${group.label} (${ownerLabel})` : group.label;
  const name = group.companyName || "Unknown company";
  const trades = (group.tradeLabels && group.tradeLabels.length ? group.tradeLabels : group.trades) || [];
  return trades.length ? `${name} - ${trades.join(", ")}` : name;
}

// Everything the search box matches: name, email, company, trade, kind.
function searchText(person, group) {
  return [
    person && person.name, person && person.email, person && person.jobTitle,
    person && KIND_LABEL[person.kind],
    group && group.companyName, groupLabel(group),
  ].filter(Boolean).join(" ").toLowerCase();
}

// The two value shapes most fields use, so callers do not each write them:
// an address (recipients, shop drawings, shares), or a user id with
// `email:<addr>` for a contact with no account (the issue / checklist /
// schedule responsible routes provision that, as they always have).
export const emailValue = (p) => String((p && p.email) || "").toLowerCase();
export const userOrEmailRef = (p) => (p && p.userId ? String(p.userId) : (p && p.email ? `email:${String(p.email).toLowerCase()}` : ""));

const defaultPersonValue = emailValue;

/**
 * antd option groups for `roster`.
 * Each option carries `person`, `group`, `search` and (if disabled) `reason`.
 */
export function peoplePickerOptions(roster, opts = {}) {
  const {
    personValue = defaultPersonValue,
    companyValue,
    noAccount = "allow",
    executives = "allow",
    nameOnly = "disable",
    note,
    ownerLabel = "project owner",
    companyOptionLabel = (g) => `Whole company - ${groupLabel(g)}`,
  } = opts;
  const seen = new Set();
  return (roster || []).map((group, gi) => {
    const options = [];
    if (typeof companyValue === "function") {
      const v = companyValue(group);
      if (v && !seen.has(v)) {
        seen.add(v);
        options.push({
          value: v,
          label: companyOptionLabel(group),
          search: `${searchText(null, group)} company`,
          group,
          isCompany: true,
        });
      }
    }
    for (const person of peopleOf(group)) {
      let reason = "";
      if (person.needsEmail && nameOnly !== "allow") {
        reason = person.reason || NEEDS_EMAIL_REASON;
      } else if (person.kind === "executive" && person.assignable === false && executives !== "allow") {
        reason = person.reason || "Executive - receives reports only, cannot be made responsible";
      } else if (!person.hasAccount && noAccount !== "allow") {
        reason = NO_ACCOUNT_REASON;
      }
      let value = reason ? "" : personValue(person, group);
      if (!value) {
        if (!reason) reason = "This field cannot hold this person";
        // A disabled row still needs a unique, inert value.
        value = `disabled:${gi}:${person.email || person.userId || person.key || person.name}`;
      }
      if (seen.has(value)) continue;
      seen.add(value);
      options.push({
        value,
        label: person.name || person.email,
        search: searchText(person, group),
        person,
        group,
        disabled: !!reason,
        ...(reason ? { reason } : {}),
        ...(typeof note === "function" && note(person, group) ? { note: note(person, group) } : {}),
      });
    }
    if (!options.length && group && group.role !== "owner") {
      options.push({
        value: `empty:${group.companyId || gi}`,
        label: EMPTY_PARTNER_LABEL,
        search: searchText(null, group),
        group,
        disabled: true,
        isEmpty: true,
        reason: EMPTY_PARTNER_REASON,
      });
    }
    return {
      label: groupLabel(group, { ownerLabel }),
      // UNIQUE PER GROUP, NOT PER COMPANY (owner, 2026-10-18: "in recipients,
      // when i add few names, the dropdown starts repeating DP Electric (Project
      // Owner)"). The owner company can also be a partner on its own project -
      // SpaceX BA03 lists DP Electric as both - and the two groups shared the key
      // g:<companyId>. The dropdown's virtual list reuses rows by key, so with
      // each pick it drew the owner header again in the other group's place and
      // the people under it went missing.
      key: `g:${gi}:${group.role || "partner"}:${group.companyId || ""}`,
      group,
      options,
    };
  }).filter((g) => g.options.length);
}

/** Every person option, flattened (for "who is this value" lookups). */
export function flatOptions(groups) {
  return (groups || []).reduce((a, g) => a.concat(g.options || []), []);
}

/**
 * antd filterOption that searches name, email, company and trade.
 *
 * Owner, 2026-10-22: dropdown search "doesn't filter select result
 * correctly". This used to be ONE SUBSTRING of `search || label`, so
 * "nina bes" found nobody even though the row reads "Nina - nina@bes.test -
 * BES Mechanical": the words are there, with other words between them. It
 * also fell through to `label`, which is JSX here, so every person matched
 * "object". Both are fixed in the shared matcher, which this now is - kept
 * as its own name because every people field already passes it by name.
 */
export function filterPeopleOption(input, option) {
  return matchOption(input, option);
}

/** The person (and group) a value stands for, or null. */
export function findPerson(roster, predicate) {
  for (const group of roster || []) {
    for (const p of peopleOf(group)) if (predicate(p, group)) return { person: p, group };
  }
  return null;
}

// WHOLE-COMPANY PICKS FOR A FIELD THAT HOLDS PEOPLE (the share dialog,
// owner 2026-10-15: pick partner companies as recipients). The company row
// is a shortcut, not a value the field can store: it is expanded here into
// that company's people, by the field's own personValue. Executives are
// left out of the expansion - a reader is named on purpose, one at a time -
// and so is anyone the field cannot hold. Returns the values in order,
// deduped.
export const COMPANY_PREFIX = "company:";
export const companyPickValue = (g) => (g && g.role !== "owner" && g.companyId ? `${COMPANY_PREFIX}${g.companyId}` : "");

export function expandCompanyPicks(values, roster, { personValue = emailValue, noAccount = "allow" } = {}) {
  const out = [];
  const seen = new Set();
  const push = (v) => { if (v && !seen.has(v)) { seen.add(v); out.push(v); } };
  for (const v of values || []) {
    const s = String(v);
    if (!s.startsWith(COMPANY_PREFIX)) { push(s); continue; }
    const cid = s.slice(COMPANY_PREFIX.length);
    // The PARTNER row, never the owner's: a company can be both on one
    // project (DP Electric owns SpaceX BA03 and is a contractor on it), and
    // companyPickValue never makes a value for the owner row - so the first
    // row with this id was the owner's staff, not the partner's people.
    const group = (roster || []).find((g) => String(g.companyId) === cid && g.role !== "owner");
    for (const p of peopleOf(group)) {
      if (p.kind === "executive") continue;
      if (p.needsEmail) continue; // nothing to send to, nobody to assign
      if (!p.hasAccount && noAccount !== "allow") continue;
      push(personValue(p, group));
    }
  }
  return out;
}

/** "Whole company - BES - Electrical (4 people)" */
export function companyOptionLabelWithCount(g) {
  const n = peopleOf(g).filter((p) => p.kind !== "executive").length;
  return `Whole company - ${groupLabel(g)} (${n} ${n === 1 ? "person" : "people"})`;
}
